import { Navigator } from '@solidjs/router';
import { getTranslationKeyFunction } from '~/utils/commonUtils.ts';
import {
  ConnectedAppProvider,
  ConnectedAppReservedProviderTypes
} from '~/api/types/connectedAppProviders.ts';
import {
  addConnectionForEnvironment,
  ApiErrorInstance,
  deleteConnectedAccountByIdentifier,
  deleteEnvironmentConnection,
  getConnectedAccountDetailsQueryKey,
  getConnectedAccountListQueryKey,
  getConnectedAppProvidersQueryKey,
  getConnectedAppsListQueryKey,
  isApi5xxResponseStatus,
  isApiError,
  toggleEnvironmentConnection,
  withHandleError
} from '~/api';
import { HttpStatusCode } from 'axios';
import { getLocalizedString } from '~/i18n/utils.ts';
import { ColumnDef } from '@tanstack/solid-table';
import { ConnectionData } from '~/api/types/connectionData.ts';
import ConnectProviderIcon from '~/pages/ScalekitConnect/commons/ConnectProviderIcon.tsx';
import { ActionsDropdown } from '~/components/ActionsDropdown';
import ConnectionStatusTag from '~/pages/ScalekitConnect/commons/ConnectionStatusTag.tsx';
import { Localized } from '~/i18n';
import {
  EnvironmentPagePath,
  getSlug,
  replacePathParams
} from '~/components/AppRouter/utils.ts';
import { ConfirmModalValues } from '~/components/ui/Modal/ConfirmModalContext.tsx';
import { ToastContextValue } from '~/components/ui/Toast';
import { createSignal, Show } from 'solid-js';
import { QueryClient } from '@tanstack/solid-query';
import logger from '~/utils/logger';
import { ConnectedAppContextValueType } from '~/pages/ScalekitConnect/commons/ConnectedAppContext.tsx';
import { AppErrors } from '~/components/ErrorBoundaries/utils.ts';
import {
  ConnectedAccountData,
  ConnectedAccountStatus
} from '~/api/types/connectedAccounts.ts';
import { ReadOnlyField } from '~/components/ui/ReadOnlyField';
import ConnectedAccountStatusTag from '~/pages/ScalekitConnect/commons/ConnectedAccountStatusTag.tsx';
import { getConnectionTypeForCreation } from '~/pages/ScalekitConnect/Integrations/Providers/utils.ts';
import { getAuthTypeForProvider } from '~/pages/ScalekitConnect/Integrations/ConnectedAppConfiguration/utils.ts';
import { AppDataContextValueType } from '~/components/AppData/AppData.tsx';
import { miscConstants } from '~/consts.ts';
import { isFeatureEnabled } from '~/utils/features/featureFlag.tsx';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import { AgentAuthTabTypes } from '~/pages/ScalekitConnect/commons/utils.ts';

const CONNECTED_APP_LIST_PAGE_SIZE = 20;
const CONNECTED_ACCOUNTS_LIST_PAGE_SIZE = 20;

export enum ConnectedAppDetailsTabTypes {
  CONNECTED_ACCOUNTS = `connected-accounts`,
  CONFIGURATION = 'configuration',
  TOOLS = 'tools'
}

const PROVIDER_LIST_PAGE_SIZE = 100;

const CONNECTED_ACCOUNTS_MIN_SEARCH_CHARACTERS = 3;

const getTranslationKey = getTranslationKeyFunction(
  `pages.scalekit_connect_integrations`
);

const getServerErrorTranslationKey = getTranslationKeyFunction(
  `server_errors.scalekit_connect_integrations`
);

const getProviderIcon = (provider: ConnectedAppProvider): string => {
  return provider?.iconSrc ?? '';
};

const createConnectedAppForProvider = async (
  provider: ConnectedAppProvider
) => {
  return await addConnectionForEnvironment({
    providerKey: provider.identifier,
    type: getConnectionTypeForCreation(provider),
    flags: {
      isApp: true
    }
  });
};

const getConnectedAppListColumnDef = ({
  intl,
  providerMap,
  onViewConnectionDetails,
  onDeleteConnection
}: {
  intl: any;
  providerMap: Record<string, ConnectedAppProvider>;
  onViewConnectionDetails: (c: ConnectionData) => void;
  onDeleteConnection: (c: ConnectionData) => Promise<void>;
}): ColumnDef<ConnectionData>[] => {
  return [
    {
      header: getLocalizedString(
        getTranslationKey('table_header_application_name'),
        { intl }
      ),
      cell: (info) => {
        const connection = info.row.original;

        return (
          // @todo revisit this logic once the provider list tends to grow beyond 100 (i.e. the page size)
          <Show
            when={providerMap[connection.providerKey]}
            fallback={`-`}
          >
            <div class={`flex gap-3 items-center`}>
              <ConnectProviderIcon
                class={`w-6`}
                provider={providerMap[connection.providerKey]}
              />
              <div class={`text-sm`}>
                {providerMap[connection.providerKey].displayName}
              </div>
            </div>
          </Show>
        );
      },
      meta: {
        class: 'w-4/12' // additional meta to control column width
      }
    },
    {
      header: getLocalizedString(
        getTranslationKey('table_header_connection_name'),
        { intl }
      ),
      accessorKey: 'keyId',
      cell: (info) => {
        return info.getValue();
      },
      meta: {
        class: 'w-4/12' // additional meta to control column width
      }
    },
    {
      header: getLocalizedString(getTranslationKey('table_header_status'), {
        intl
      }),
      accessorKey: 'enabled',
      cell: (info) => {
        return <ConnectionStatusTag connectedApp={info.row.original} />;
      },
      meta: {
        class: 'w-3/12' // additional meta to control column width
      }
    },

    {
      header: '',
      id: 'actions',
      cell: (info) => {
        const connection = info.row.original;
        const [isDropdownLoading, setIsDropdownLoading] = createSignal(false);
        return (
          <span
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ActionsDropdown
              loading={isDropdownLoading()}
              id={`actions-dropdown-${connection.id}`}
              items={[
                {
                  label: getLocalizedString(
                    getTranslationKey(
                      'dropdown_action_view_connection_details'
                    ),
                    {
                      intl
                    }
                  ),
                  onClick: () => onViewConnectionDetails(connection),
                  dataTestId: 'connect-connection-view-details'
                },
                {
                  label: (
                    <span class={`text-error`}>
                      <Localized
                        translationKey={getTranslationKey(
                          'dropdown_action_delete_connection'
                        )}
                        vars={{ intl }}
                      />
                    </span>
                  ),
                  dataTestId: 'connect-connection-delete-modal',
                  onClick: async () => {
                    setIsDropdownLoading(true);
                    try {
                      await onDeleteConnection(connection);
                    } catch (_) {
                    } finally {
                      setIsDropdownLoading(false);
                    }
                  }
                }
              ]}
            />
          </span>
        );
      },
      meta: {
        class: 'w-1/12' // additional meta to control column width
      }
    }
  ];
};

const handleCreateConnectionError = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    const errorCode = serverError.error?.details?.[0]?.error_code;
    if (errorCode === AppErrors.RESTRICTED_CONNECTION_NAME) {
      return getLocalizedString(
        getServerErrorTranslationKey('restricted_connection_name'),
        {
          intl
        }
      );
    }
    return getLocalizedString(
      getServerErrorTranslationKey(`create_connection_failed`),
      {
        intl
      }
    );
  }
});

const handleUpdateConnectionError = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    const errorCode = serverError.error?.details?.[0]?.error_code;
    if (errorCode === AppErrors.RESTRICTED_CONNECTION_NAME) {
      return getLocalizedString(
        getServerErrorTranslationKey('restricted_connection_name'),
        {
          intl
        }
      );
    }
    return getLocalizedString(
      getServerErrorTranslationKey(`update_connection_failed`),
      {
        intl
      }
    );
  }
});

const handleCreateConnectedAccountError = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  const errorCode = serverError.error?.details?.[0]?.error_code;
  if (errorCode === AppErrors.RESOURCE_ALREADY_EXISTS) {
    return getLocalizedString(
      getServerErrorTranslationKey(`connected_account_already_exists`),
      {
        intl
      }
    );
  }
  if (errorCode === AppErrors.INVALID_REDIRECT_URI) {
    return getLocalizedString(
      getServerErrorTranslationKey(`set_valid_redirect_uri`),
      {
        intl
      }
    );
  }
  if (errorCode === AppErrors.CONNECTION_NOT_ENABLED) {
    return getLocalizedString(
      getServerErrorTranslationKey(`connection_not_enabled`),
      {
        intl
      }
    );
  }
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    return getLocalizedString(
      getServerErrorTranslationKey(`create_connected_account_failed`),
      {
        intl
      }
    );
  }
});

const handleUpdateConnectedAccountError = withHandleError(({ error, intl }) => {
  const serverError = error as ApiErrorInstance;
  const errorCode = serverError.error?.details?.[0]?.error_code;
  if (errorCode === AppErrors.INVALID_REDIRECT_URI) {
    return getLocalizedString(
      getServerErrorTranslationKey(`set_valid_redirect_uri`),
      {
        intl
      }
    );
  }
  if (errorCode === AppErrors.CONNECTION_NOT_ENABLED) {
    return getLocalizedString(
      getServerErrorTranslationKey(`connection_not_enabled`),
      {
        intl
      }
    );
  }
  if (
    serverError.httpStatus === HttpStatusCode.BadRequest ||
    isApi5xxResponseStatus(serverError.httpStatus)
  ) {
    return getLocalizedString(
      getServerErrorTranslationKey(`update_connected_account_failed`),
      {
        intl
      }
    );
  }
});

const navigateToConnectionList = ({
  environmentId,
  navigate
}: {
  environmentId: string;
  navigate: Navigator;
}) => {
  const navLink = isFeatureEnabled(FeatureFlagKeys.SCALEKIT_AGENT_AUTH)
    ? getSlug.environmentById(
        environmentId,
        replacePathParams(EnvironmentPagePath.SCALEKIT_AGENT_AUTH_TABS, {
          tabType: AgentAuthTabTypes.CONNECTIONS
        }) as EnvironmentPagePath,
        true
      )
    : getSlug.environmentById(
        environmentId,
        EnvironmentPagePath.SCALEKIT_CONNECT_INTEGRATIONS,
        true
      );
  navigate(navLink);
};

const navigateToConnectionDetails = ({
  navigate,
  connectionId,
  environmentId,
  tabType = ConnectedAppDetailsTabTypes.CONFIGURATION
}: {
  connectionId: string;
  navigate: Navigator;
  environmentId: string;
  tabType?: ConnectedAppDetailsTabTypes;
}) => {
  navigate(
    getSlug.environmentById(
      environmentId,
      replacePathParams(
        isFeatureEnabled(FeatureFlagKeys.SCALEKIT_AGENT_AUTH)
          ? EnvironmentPagePath.SCALEKIT_AGENT_AUTH_CONNECTION_DETAILS
          : EnvironmentPagePath.SCALEKIT_CONNECT_INTEGRATION_DETAILS,
        {
          connectionId: connectionId,
          tabType
        }
      ) as EnvironmentPagePath,
      true
    )
  );
};

const openEditConnectedAccountInNewTab = ({
  appData,
  account
}: {
  appData: AppDataContextValueType;
  account: ConnectedAccountData;
}) => {
  const connectionDetailsConnectedAccountsListPath = replacePathParams(
    getSlug.environmentById(
      appData.selectedEnvironment!.id,
      isFeatureEnabled(FeatureFlagKeys.SCALEKIT_AGENT_AUTH)
        ? EnvironmentPagePath.SCALEKIT_AGENT_AUTH_CONNECTION_DETAILS
        : EnvironmentPagePath.SCALEKIT_CONNECT_INTEGRATION_DETAILS,
      true
    ) as EnvironmentPagePath,
    {
      connectionId: account.connectionId,
      tabType: ConnectedAppDetailsTabTypes.CONNECTED_ACCOUNTS
    }
  );
  const queryParams = new URLSearchParams({
    [miscConstants.QUERY_PARAMS.CONNECTED_ACCOUNT_IDENTIFIER]:
      account.identifier,
    [miscConstants.QUERY_PARAMS.ACTION]:
      miscConstants.QUERY_PARAMS.ACTION_VALUES.EDIT
  });
  window.open(
    `${connectionDetailsConnectedAccountsListPath}?${queryParams.toString()}`,
    '_blank'
  );
};

const deleteConnectedAppWithConfirm = async ({
  connection,
  confirm,
  intl,
  provider,
  toaster,
  queryClient
}: {
  connection: ConnectionData;
  confirm: ConfirmModalValues;
  intl: any;
  provider: ConnectedAppProvider;
  toaster: ToastContextValue;
  queryClient: QueryClient;
}): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    confirm.show({
      title: getLocalizedString(
        getTranslationKey(`delete_connection_confirm_header`),
        { intl }
      ),
      description: getLocalizedString(
        getTranslationKey('delete_connection_confirm_message'),
        {
          providerName: provider.displayName,
          intl
        }
      ),
      primaryActionLabel: getLocalizedString('cta_labels.delete', {
        intl
      }),
      primaryActionDataTestId: 'connect-connection-delete',
      onConfirm: async () => {
        try {
          confirm.setIsLoading(true);
          await deleteEnvironmentConnection(connection.id);
          toaster.toast().success({
            description: getLocalizedString(
              getTranslationKey(`delete_connection_success_toast`),
              { intl, connectionName: connection.keyId }
            )
          });
          confirm.close();
          await queryClient.refetchQueries({
            queryKey: getConnectedAppsListQueryKey(),
            type: 'all',
            exact: true
          });
          resolve(true);
        } catch (e) {
          if (
            isApiError(e) &&
            e.error?.details?.[0]?.error_code ===
              AppErrors.CONNECTION_ACCOUNT_EXISTS_FOR_CONNECTION
          ) {
            toaster.toast().error({
              description: getLocalizedString(
                getTranslationKey(`connection_cant_be_deleted_accounts_exist`),
                { intl, connectionName: connection.keyId }
              )
            });
          } else {
            toaster.toast().error({
              description: getLocalizedString(
                getTranslationKey(`delete_connection_failure_toast`),
                { intl, connectionName: connection.keyId }
              )
            });
          }
          reject(e);
        } finally {
          confirm.setIsLoading(false);
        }
      },
      onCancel: () => {
        resolve(false);
      }
    });
  });
};

const toggleConnectedAppStatus = async ({
  toaster,
  intl,
  queryClient,
  connectedAppContext
}: {
  toaster: ToastContextValue;
  intl: any;
  queryClient: QueryClient;
  connectedAppContext: ConnectedAppContextValueType;
}) => {
  const connection = connectedAppContext.connectedApp!;
  try {
    await toggleEnvironmentConnection(connection.id, !connection.enabled);
    await connectedAppContext.refetchConnectedApp();
    toaster.toast().success({
      description: getLocalizedString(
        getTranslationKey(
          connection.enabled
            ? `disable_connection_success_toast`
            : `enable_connection_success_toast`
        ),
        { intl }
      )
    });
    queryClient.invalidateQueries({
      queryKey: getConnectedAppProvidersQueryKey(),
      type: 'all',
      exact: true
    });
  } catch (e) {
    toaster.toast().error({
      description: getLocalizedString(
        getTranslationKey(
          connection.enabled
            ? `disable_connection_failure_toast`
            : `enable_connection_failure_toast`
        ),
        { intl }
      )
    });
  }
};

const confirmAndToggleConenctedAppStatus = async ({
  confirm,
  toaster,
  intl,
  queryClient,
  connectedAppContext
}: {
  confirm: ConfirmModalValues;
  toaster: ToastContextValue;
  intl: any;
  queryClient: QueryClient;
  connectedAppContext: ConnectedAppContextValueType;
}) => {
  const connection = connectedAppContext.connectedApp!;
  try {
    if (connection.enabled) {
      confirm.show({
        title: getLocalizedString(
          getTranslationKey('disable_connection_confirm_header'),
          { intl }
        ),
        description: getLocalizedString(
          getTranslationKey('disable_connection_confirm_message'),
          { intl }
        ),
        primaryActionDataTestId: 'connect-connection-disable',
        onConfirm: async () => {
          confirm.setIsLoading(true);
          await toggleConnectedAppStatus({
            toaster,
            intl,
            queryClient,
            connectedAppContext
          });
          confirm.setIsLoading(false);
          confirm.close();
        }
      });
      return;
    }
    await toggleConnectedAppStatus({
      toaster,
      intl,
      queryClient,
      connectedAppContext
    });
  } catch (e) {
    logger.error(e);
  }
};

const getConnectedAccountListColumnDef = ({
  connectedApp,
  connectedAppProvider,
  intl,
  onReauthorizeConnectedAccount,
  onEditConnectedAccount,
  onDeleteAccount,
  providersMapById
}: {
  connectedApp: ConnectionData | null;
  connectedAppProvider: ConnectedAppProvider | null;
  intl: any;
  onReauthorizeConnectedAccount: (
    connectedAccount: ConnectedAccountData
  ) => Promise<void>;
  onEditConnectedAccount: (
    connectedAccount: ConnectedAccountData
  ) => Promise<void>;
  onDeleteAccount: (c: ConnectedAccountData) => Promise<void>;
  providersMapById?: Record<string, ConnectedAppProvider> | null;
}): ColumnDef<ConnectedAccountData>[] => {
  return [
    {
      header: getLocalizedString(
        getTranslationKey('table_header_connected_account_identifier'),
        { intl }
      ),
      accessorKey: 'identifier',
      cell: (info) => {
        return (
          <ReadOnlyField
            value={info.getValue() as string}
            variant={'ghost'}
            containerClass={`px-0`}
          />
        );
      },
      meta: {
        class: 'w-3/12' // additional meta to control column width
      }
    },
    {
      header: getLocalizedString(
        getTranslationKey('table_header_connected_account_id'),
        { intl }
      ),
      accessorKey: 'id',
      cell: (info) => {
        return (
          <ReadOnlyField
            value={info.getValue() as string}
            variant={'ghost'}
            containerClass={`px-0`}
          />
        );
      },
      meta: {
        class: 'w-3/12' // additional meta to control column width
      }
    },
    {
      header: getLocalizedString(
        getTranslationKey('table_header_connected_account_application_name'),
        { intl }
      ),
      cell: (info) => {
        const account = info.row.original;
        const provider =
          connectedAppProvider ?? providersMapById?.[account.provider];
        return (
          <div class={`flex gap-3 items-center`}>
            <Show when={provider}>
              <ConnectProviderIcon
                class={`w-6`}
                provider={provider!}
              />
            </Show>
            <div class={`text-sm`}>
              {connectedApp?.keyId ?? account.connector}
            </div>
          </div>
        );
      },
      meta: {
        class: 'w-3/12' // additional meta to control column width
      }
    },
    {
      header: getLocalizedString(
        getTranslationKey('table_header_connected_account_status'),
        {
          intl
        }
      ),
      accessorKey: 'enabled',
      cell: (info) => {
        return <ConnectedAccountStatusTag account={info.row.original} />;
      },
      meta: {
        class: 'w-2/12' // additional meta to control column width
      }
    },
    {
      header: '',
      id: 'actions',
      cell: (info) => {
        const account = info.row.original;
        const provider =
          connectedAppProvider || providersMapById?.[account.provider];
        const [isDropdownLoading, setIsDropdownLoading] = createSignal(false);
        return (
          <ActionsDropdown
            loading={isDropdownLoading()}
            id={`actions-dropdown-${account.identifier}`}
            items={[
              {
                label: getLocalizedString(
                  getTranslationKey(
                    account.status === ConnectedAccountStatus.ACTIVE
                      ? 'dropdown_action_reauthorize_connected_account_details'
                      : 'dropdown_action_authorize_connected_account_details'
                  ),
                  {
                    intl
                  }
                ),
                onClick: async () => {
                  await onReauthorizeConnectedAccount(account);
                },
                hidden:
                  account.authorizationType !==
                  ConnectedAppReservedProviderTypes.OAUTH2,
                dataTestId:
                  account.status === ConnectedAccountStatus.ACTIVE
                    ? 'connect-connected-account-reauthorize'
                    : 'connect-connected-account-authorize'
              },
              {
                label: getLocalizedString(
                  getTranslationKey(
                    'dropdown_action_update_connected_account_auth_config'
                  ),
                  {
                    intl
                  }
                ),
                onClick: async () => {
                  setIsDropdownLoading(true);
                  await onEditConnectedAccount(account);
                  setIsDropdownLoading(false);
                },
                hidden:
                  account.authorizationType ===
                    ConnectedAppReservedProviderTypes.OAUTH2 &&
                  (!provider ||
                    !getAuthTypeForProvider({
                      provider,
                      authType: account.authorizationType
                    })?.accountFields?.length),
                dataTestId: 'connect-connected-account-edit'
              },
              {
                label: (
                  <span class={`text-error`}>
                    <Localized
                      translationKey={getTranslationKey(
                        'dropdown_action_delete_connected_account'
                      )}
                      vars={{ intl }}
                    />
                  </span>
                ),
                dataTestId: 'connect-connected-account-delete-modal',
                onClick: async () => {
                  setIsDropdownLoading(true);
                  await onDeleteAccount(account);
                  setIsDropdownLoading(false);
                }
              }
            ]}
          />
        );
      },
      meta: {
        class: 'w-1/12' // additional meta to control column width
      }
    }
  ];
};

const refetchConnectedAccountList = async ({
  queryClient,
  connector
}: {
  queryClient: QueryClient;
  connector?: string;
}) => {
  return await queryClient.refetchQueries({
    queryKey: getConnectedAccountListQueryKey(connector),
    type: 'all',
    exact: true
  });
};

const refetchDefaultWorkspaceConnectedAccount = async ({
  queryClient,
  connector,
  identifier
}: {
  queryClient: QueryClient;
  connector: string;
  identifier: string;
}) => {
  return await queryClient.refetchQueries({
    queryKey: getConnectedAccountDetailsQueryKey({
      connector,
      identifier
    }),
    type: 'all',
    exact: true
  });
};

const deleteConnectedAccountWithConfirm = async ({
  appData,
  account,
  confirm,
  intl,
  toaster,
  queryClient
}: {
  appData: AppDataContextValueType;
  account: ConnectedAccountData;
  confirm: ConfirmModalValues;
  intl: any;
  toaster: ToastContextValue;
  queryClient: QueryClient;
}): Promise<boolean> => {
  return new Promise((resolve) => {
    confirm.show({
      title: getLocalizedString(
        getTranslationKey('delete_connected_account_confirm_header'),
        { intl }
      ),
      description: getLocalizedString(
        getTranslationKey('delete_connected_account_confirm_message'),
        {
          intl,
          connectedAccountIdentifier: account.identifier
        }
      ),
      primaryActionLabel: getLocalizedString('cta_labels.delete', { intl }),
      primaryActionDataTestId: 'connect-connected-account-delete',
      onConfirm: async () => {
        try {
          confirm.setIsLoading(true);
          await deleteConnectedAccountByIdentifier({
            account
          });
          toaster.toast().success({
            description: getLocalizedString(
              getTranslationKey('delete_connected_account_success_toast'),
              { intl, connectedAccountIdentifier: account.identifier }
            )
          });
          confirm.close();
          await refetchConnectedAccountList({
            queryClient,
            connector: account.connector
          });
          if (account.identifier === appData.member?.email!) {
            // Refetch the connected account details
            // if the deleted account is the current user's account
            // this is used in the playground
            await refetchDefaultWorkspaceConnectedAccount({
              queryClient,
              connector: account.connector,
              identifier: appData.member?.email!
            });
          }
          resolve(true);
        } catch (e) {
          let errorMessage = '';
          if (
            isApiError(e) &&
            e.error.details?.[0]?.error_code ===
              AppErrors.MCP_SERVER_EXISTS_FOR_CONNECTED_ACCOUNT
          ) {
            errorMessage = getLocalizedString(
              getTranslationKey(
                'delete_connected_account_mcp_mapped_failure_toast'
              ),
              { intl, connectedAccountIdentifier: account.identifier }
            );
          } else {
            errorMessage = getLocalizedString(
              getTranslationKey('delete_connected_account_failure_toast'),
              { intl, connectedAccountIdentifier: account.identifier }
            );
          }
          toaster.toast().error({
            description: errorMessage
          });
          logger.error(`Error deleting connected account: `, e);
          resolve(false);
        } finally {
          confirm.setIsLoading(false);
        }
      },
      onCancel: () => {
        resolve(false);
      }
    });
  });
};

const getAppTitle = ({
  intl,
  tabType,
  connectionName
}: {
  intl: any;
  tabType: ConnectedAppDetailsTabTypes;
  connectionName?: string;
}) => {
  const titleMap: Record<ConnectedAppDetailsTabTypes, string> = {
    [ConnectedAppDetailsTabTypes.CONFIGURATION]: getLocalizedString(
      'app_title.agent_auth_connection_configuration_app_title',
      { intl, connectionName }
    ),
    [ConnectedAppDetailsTabTypes.CONNECTED_ACCOUNTS]: getLocalizedString(
      'app_title.agent_auth_connection_connected_account_app_title',
      { intl, connectionName }
    ),
    [ConnectedAppDetailsTabTypes.TOOLS]: getLocalizedString(
      'app_title.agent_auth_connection_tools_app_title',
      { intl, connectionName }
    )
  };

  return (
    (tabType && titleMap?.[tabType]) ||
    getLocalizedString('page_titles.connect_integrations', { intl })
  );
};

export {
  CONNECTED_APP_LIST_PAGE_SIZE,
  CONNECTED_ACCOUNTS_LIST_PAGE_SIZE,
  PROVIDER_LIST_PAGE_SIZE,
  CONNECTED_ACCOUNTS_MIN_SEARCH_CHARACTERS,
  getTranslationKey,
  getServerErrorTranslationKey,
  getProviderIcon,
  createConnectedAppForProvider,
  getConnectedAppListColumnDef,
  handleCreateConnectionError,
  handleUpdateConnectionError,
  handleCreateConnectedAccountError,
  handleUpdateConnectedAccountError,
  navigateToConnectionList,
  navigateToConnectionDetails,
  openEditConnectedAccountInNewTab,
  deleteConnectedAppWithConfirm,
  confirmAndToggleConenctedAppStatus,
  getConnectedAccountListColumnDef,
  refetchConnectedAccountList,
  deleteConnectedAccountWithConfirm,
  getAppTitle
};
